import React, { useMemo, useCallback, useState, useEffect } from "react";
import { IconButton } from "@fluentui/react";
import { Info16Regular } from "@fluentui/react-icons";
import {
    Search16Regular,
    Copy16Regular,
    Flag16Regular,
    ChevronDown16Regular
} from "@fluentui/react-icons";
import DOMPurify from "dompurify";
import { useTranslation } from "../../i18n/useTranslation";
import { useLocation } from "react-router-dom";

import styles from "./Answer.module.css";
import { ChatAppResponse } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { Button } from "@fluentui/react-components";
import sendIcon from "../../assets/sendicon.png";
import linkIcon from "../../assets/external-link.svg";
import copyIcon from "../../assets/copy.svg";
import flagIcon from "../../assets/flag.svg";
import infoIcon from "../../assets/info.svg";
import downloadIcon from "../../assets/download.svg";
import searchIcon from "../../assets/search.svg";
import { Citation } from "../Answer/types";
import { trackLinkClick, trackDownload } from "../../analytics";

interface DataPoint {
    id: string;
    content: string;
    source_id: string;
    metadata: {
        document_url: string;
        document_guid: string;
        last_modified_date: string;
        file_name: string;
        title: string;
        type: string;
    };
}

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

function buildCitationMap(dataPoints: DataPoint[]): Map<string, string> {
    const map = new Map<string, string>();
    const counts = new Map<string, number>();

    for (const dp of dataPoints) {
        const sid = dp.source_id;
        if (!sid) continue;

        const base =
            (dp.metadata.title && dp.metadata.title.trim()) ||
            (dp.metadata.file_name && dp.metadata.file_name.trim()) ||
            sid;

        const n = (counts.get(base) ?? 0) + 1;
        counts.set(base, n);
        const label = n === 1 ? base : `${base} (${n})`;

        if (!map.has(sid)) map.set(sid, label);
    }
    return map;
}

// NEW: map [source_#] -> [<chunkId>]
function buildChunkIdMap(dataPoints: DataPoint[]): Map<string, string> {
    const map = new Map<string, string>();
    const counts = new Map<string, number>();

    for (const dp of dataPoints) {
        const sid = dp.source_id;
        if (!sid) continue;

        const base = dp.id?.trim() || sid;
        const n = (counts.get(base) ?? 0) + 1;
        counts.set(base, n);

        // If the same chunk id appears multiple times (rare), disambiguate
        const label = n === 1 ? base : `${base} (${n})`;

        if (!map.has(sid)) map.set(sid, label);
    }
    return map;
}

function replaceSourceIdsWithFilenames(text: string, citationMap: Map<string, string>): string {
    // Replace [source_1] to [MyDoc.pdf]
    return text.replace(/\[(source_\d+)\]/g, (m, sid) => {
        const label = citationMap.get(sid);
        return label ? `[${label}]` : m;
    });
}

// NEW: Replace [source_1] -> [<chunkId>]
function replaceSourceIdsWithChunkIds(text: string, chunkIdMap: Map<string, string>): string {
    return text.replace(/\[(source_\d+)\]/g, (m, sid) => {
        const label = chunkIdMap.get(sid);
        return label ? `[${label}]` : m;
    });
}

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
}: Props) => {
    const { t, lang } = useTranslation();
    const location = useLocation();

    // 0) parse raw data_points JSON into typed DataPoint[]
    const rawDp = answer.choices[0].context.data_points;
    const rawDataPoints: string[] = Array.isArray(rawDp)
        ? rawDp
        : Array.isArray((rawDp as any)?.text)
            ? (rawDp as any).text
            : [];
    const dataPoints: DataPoint[] = rawDataPoints
        .map(txt => {
            try {
                return JSON.parse(txt) as DataPoint;
            } catch {
                return null;
            }
        })
        .filter((dp): dp is DataPoint => dp !== null);

    // 1) extract original user prompt
    const userPrompt = useMemo(() => {
        const thoughts: Array<{ title: string; description: string }> =
            (answer.choices[0].context as any).thoughts || [];
        const orig = thoughts.find(t => t.title === "Original User Query");
        return orig?.description.trim() ?? "";
    }, [answer]);

    const ragQueryPrompt = useMemo(() => {
        const thoughts: Array<{ title: string; description: string }> =
            (answer.choices[0].context as any).thoughts || [];
        return thoughts.find(t => t.title === "RAG Query Prompt")?.description?.trim() ?? "";
    }, [answer]);

    // 2) generated answer + parse citations
    const generatedAnswer = answer.choices[0].message.content;
    const { answerHtml, citations } = useMemo(
        () =>
            parseAnswerToHtml(
                generatedAnswer,
                isStreaming,
                dataPoints,
                onCitationClicked
            ),
        [generatedAnswer, isStreaming, dataPoints, onCitationClicked]
    );
    const safeHtml = DOMPurify.sanitize(answerHtml);

    // 3) build "Quellen" list as "chunk id: title"
    const dataPointsList = useMemo(() => {
        if (!dataPoints.length) return "- (keine Quellen)";
        return dataPoints
            .map(dp => `- ${dp.id}`)
            .join("\n");
    }, [dataPoints]);

    const citationMap = useMemo(() => buildCitationMap(dataPoints), [dataPoints]);
    const chunkIdMap = useMemo(() => buildChunkIdMap(dataPoints), [dataPoints]);

    // tooltip
    const tooltipLines = t("answer.tooltip").split("\n");

    useEffect(() => {
        const els = document.querySelectorAll<HTMLSpanElement>(".supContainer");
        els.forEach(el => {
            const url = el.dataset.url;
            const handler = (e: MouseEvent) => {
                e.preventDefault();
                if (url) window.open(url, "_blank");
            };
            el.addEventListener("click", handler);
            el.addEventListener("keypress", (e: KeyboardEvent) => {
                if (e.key === "Enter") handler(e as any);
            });
            (el as any)._handler = handler;
        });
        return () => {
            els.forEach(el =>
                el.removeEventListener("click", (el as any)._handler)
            );
        };
    }, [answerHtml]);

    // copy to clipboard (kept as-is: filenames)
    const handleCopy = useCallback(() => {
        const textForCopy = replaceSourceIdsWithFilenames(generatedAnswer, citationMap);
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(textForCopy);
        } else {
            const ta = document.createElement("textarea");
            ta.value = textForCopy;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
        }
    }, [generatedAnswer, citationMap]);

    // report form
    const [isReporting, setIsReporting] = useState(false);
    const [reportText, setReportText] = useState("");

    // followups & toggle
    const followups = answer.choices[0].context.followup_questions || [];
    const [showAll, setShowAll] = useState(false);

    // analytics builder
    const buildLinkOpts = () => ({
        pageName: `${lang}:lifenet:ai-search`,
        title: document.title,
        url: window.location.origin + location.pathname,
        lang,
        domain: window.location.host,
        fullUrl: window.location.href,
    });

    // citation card renderer
    const renderCitationCard = (cit: Citation, idx: number) => {
        const quickUrl = cit.url;
        const label = cit.title;
        const isAsset = cit.type === "ASSET";
        const handleClick = () => {
            const opts = buildLinkOpts();
            if (isAsset) trackDownload(label, quickUrl, opts);
            else trackLinkClick(label, quickUrl, opts);
        };

        return (
            <a
                key={idx}
                href={quickUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClick}
                className={styles.citationCard}
                data-type={cit.type}
            >
                <div className={styles.cardHeader}>
                    <span className={styles.cardNumber}>{idx + 1}</span>
                    <div className={styles.linkIcon}>
                        <img
                            src={cit.type === "ASSET" ? downloadIcon : linkIcon}
                            alt={cit.type === "ASSET" ? "Download asset" : "Open source"}
                        />
                    </div>
                </div>
                <div className={styles.cardTitle}>{cit.title}</div>
                <div className={styles.cardFooter}>
                    <span className={styles.cardDate}>
                        {cit.lastModified ? new Date(cit.lastModified).toLocaleDateString("de-CH") : "�"}
                    </span>
                </div>
            </a>
        );
    };

    return (
        <div className={`${styles.answerContainer} ${isSelected ? styles.selected : ""}`}>
            {/* Header */}
            <div className={styles.answerHeader}>
                <span>{t("answer.subheading")}</span>
                <div className={styles.tooltipInfoWrapper} tabIndex={0}>
                    <img src={infoIcon} alt="Info" className={styles.infoIcon} />
                    <div className={styles.tooltipInfoText}>
                        {tooltipLines.map((line, i) => (
                            <p key={i} className={styles.tooltipInfoLine}>{line}</p>
                        ))}
                    </div>
                </div>
            </div>

            {/* Body */}
            <div
                className={styles.answerText}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
            />

            {/* Copy / Report */}
            <div className={styles.answerActions}>
                <button className={styles.actionLink} onClick={handleCopy}>
                    <img src={copyIcon} alt={t("button.copy")} className={styles.actionIcon} />
                    {t("button.copy")}
                </button>
                <button className={styles.actionLink} onClick={() => setIsReporting(true)}>
                    <img src={flagIcon} alt={t("button.report")} className={styles.actionIcon} />
                    {t("button.report")}
                </button>
            </div>

            {/* Report Form */}
            {isReporting && (
                <div className={styles.reportForm}>
                    <textarea
                        className={styles.reportTextarea}
                        placeholder={t("report.prompt")}
                        value={reportText}
                        onChange={e => setReportText(e.target.value)}
                    />
                    <div className={styles.reportButtons}>
                        <button className={styles.cancelReport} onClick={() => setIsReporting(false)}>
                            {t("button.cancel")}
                        </button>
                        <Button
                            size="large"
                            icon={<img src={sendIcon} alt={t("button.send")} className={styles.sendIcon} />}
                            onClick={() => {
                                const mode = import.meta.env.MODE; // "d" | "q" | "p"
                                const prefix =
                                    mode === "d" ? "DEV: " :
                                        mode === "q" ? "UAT: " :
                                            "";
                                const subjectRaw = `${prefix}${"Benutzer Feedback - AI Search"}`;
                                const subject = encodeURIComponent(subjectRaw);

                                // NEW: answer text for report uses chunk ids in [brackets]
                                const answerForReport = replaceSourceIdsWithChunkIds(generatedAnswer, chunkIdMap);

                                const fullBody =
                                    `${reportText}\n\n` +
                                    `Generated Context:\n` +
                                    `Prompt:\n${userPrompt}\n\n` +
                                    `RAG Query:\n${ragQueryPrompt || "�"}\n\n` +
                                    `Antwort:\n${answerForReport}\n\n` +
                                    `Gefundene Azure AI Search Chunks:\n${dataPointsList}`;

                                const body = encodeURIComponent(fullBody).replace(/%0A/g, "%0D%0A");
                                window.open(`mailto:intranet.search@swisslife.com?subject=${subject}&body=${body}`);
                                setIsReporting(false);
                            }}
                        >
                        </Button>
                    </div>
                </div>
            )}

            {/* Citations */}
            {citations.length > 0 && (
                <div className={styles.citationSection}>
                    <div className={styles.citationHeader}>
                        <span>{t("sources.subheading")}</span>
                        <span className={styles.circleNumber}>{citations.length}</span>
                        {citations.length > 4 && (
                            <ChevronDown16Regular
                                className={`${styles.chevronIcon} ${showAll ? styles.chevronOpen : ""}`}
                                onClick={() => setShowAll(!showAll)}
                            />
                        )}
                    </div>
                    <div className={styles.citationCards}>
                        {citations.slice(0, 4).map(renderCitationCard)}
                    </div>
                    {showAll && (
                        <div className={styles.citationCards}>
                            {citations.slice(4).map((c, i) => renderCitationCard(c, i + 4))}
                        </div>
                    )}
                </div>
            )}

            {/* Follow-up Questions */}
            {showFollowupQuestions && followups.length > 0 && onFollowupQuestionClicked && (
                <div className={styles.followupBlock}>
                    <div className={styles.followupHeader}>{t("followup.heading") + ":"}</div>
                    <ul>
                        {followups.map((q, i) => (
                            <li key={i}>
                                <img src={searchIcon} alt="Search" className={styles.followupIcon} />
                                <a onClick={() => onFollowupQuestionClicked(q)}>{q}</a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
